import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useAuth } from '@/features/auth/auth-store';
import {
  getMe,
  updateMe,
  uploadAvatar,
  changePassword,
  getCurrencies,
} from './api';
import type { UpdateMeBody } from './api';

export function useMe() {
  return useQuery({
    queryKey: ['me'],
    queryFn: getMe,
    staleTime: 0,
  });
}

export function useCurrencies() {
  return useQuery({
    queryKey: ['currencies'],
    queryFn: getCurrencies,
    staleTime: 5 * 60_000,
  });
}

export function useUpdateMe() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: UpdateMeBody) => updateMe(body),
    onSuccess: (data, variables) => {
      void qc.invalidateQueries({ queryKey: ['me'] });
      if (variables.preferences) {
        void qc.invalidateQueries({ queryKey: ['listings'] });
      }
      const existing = useAuth.getState().user;
      if (existing) {
        useAuth.getState().set({
          user: {
            ...existing,
            ...(data.full_name !== undefined && { full_name: data.full_name }),
            ...(data.avatar_url !== undefined && { avatar_url: data.avatar_url }),
          },
        });
      }
    },
  });
}

export function useUploadAvatar() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (file: File) => uploadAvatar(file),
    onSuccess: (data) => {
      void qc.invalidateQueries({ queryKey: ['me'] });
      const existing = useAuth.getState().user;
      if (existing) {
        useAuth.getState().set({
          user: {
            ...existing,
            ...(data.avatar_url !== undefined && { avatar_url: data.avatar_url }),
          },
        });
      }
    },
  });
}

export function useChangePassword() {
  return useMutation({
    mutationFn: changePassword,
  });
}
